import { Box, Pagination } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../backend/ApiClient'
import { IPagination } from '../definitions/Common'
import { IAttributedMembersFilter } from '../definitions/Members'
import Helper from '../helpers/Helper'
import TimeHelper from '../helpers/TimeHelper'
import { useAppSelector } from '../store/Hooks'
import { changeRoute } from '../store/slices/SessionSlice'
import SmartTable from './controls/table/SmartTable'
import useQuery, { buildQueryKey } from './hooks/useQuery'
import Tile from './layout/Tile'
import Formatter from '../helpers/Formatter'
import { SmartTableUtilities } from './controls/table'

interface P {
    kpiLoading?: boolean
    contractId: number
    filter?: IAttributedMembersFilter
}

function AttributedMembersTable({ filter, contractId, kpiLoading }: P) {
    const dispatch = useDispatch()
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [pagination, setPagination] = useState<IPagination>({ page: 0, batch: 20 })

    useEffect(() => {
        setPagination({ ...pagination, page: 0 })
    }, [filter])

    const { status, response } = useQuery({
        queryKey: buildQueryKey('attributed-members', TimeHelper.stringify(time), pagination.page, pagination.batch, Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getAttributedMembers(contractId, time, pagination, filter),
        cache: true
    })

    const { response: attributedMembersKPI } = useQuery({
        queryKey: buildQueryKey('attributed-members-kpi', TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getAttributedMembersKPI(contractId, time, filter),
        cache: true
    })

    const isLoading = status === 'loading' || kpiLoading
    const totalPage = Math.floor((attributedMembersKPI?.totalAttributedMembers ?? 1) / pagination.batch)
    const lastPageHaveValues = (attributedMembersKPI?.totalAttributedMembers ?? 1) % pagination.batch != 0

    // TODO implement if the frontend has permission to see sensitive data (such as member name)
    const havePermissions = false

    return (
        <Tile.Container>
            <Tile.Row spacing={3} style={{ overflowY: 'auto', display: 'table' }}>
                <Box sx={{ height: '100%', overflowY: 'auto', marginBlock: 1 }}>
                    <SmartTable
                        columns={[
                            { id: 'uid', label: 'UID' },
                            { id: 'name', label: 'Name', blur: !havePermissions },
                            { id: 'startDate', label: 'Start Date' },
                            { id: 'endDate', label: 'End Date' },
                            { id: 'riskScore', label: 'Risk Score' },
                            { id: 'pmpm', label: 'PMPM' }
                        ]}
                        isLoading={isLoading}
                        sticky
                        noCollapse
                        onClickRow={i => dispatch(changeRoute({ route: 'member-details', params: { memberId: response[i].memberId } }))}
                        datasource={{
                            rows: (response ?? []).map((x, i) => {
                                return SmartTableUtilities.toRow([
                                    { value: x?.memberId },
                                    { value: havePermissions ? x?.name : 'placeholder placeholder' },
                                    { value: Formatter.date(x?.startDate) },
                                    { value: Formatter.date(x?.endDate) },
                                    { value: x?.riskScore },
                                    { value: x?.pmpm }
                                ], i)
                            })
                        }}
                    />
                </Box>
                <Box display='flex' justifyContent='flex-end'>
                    <Pagination
                        key={pagination.page}
                        count={totalPage + (lastPageHaveValues ? 1 : 0)}
                        defaultPage={(pagination.page + 1)}
                        color='primary'
                        disabled={isLoading}
                        onChange={(_, value) => setPagination(p => {
                            return { batch: p.batch, page: value - 1 }
                        })}
                    />
                </Box>
            </Tile.Row>
        </Tile.Container>
    )
}

export default AttributedMembersTable
